
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { Action, Getter, Mutation } from 's-vuex-class'
import qs from 'qs'
import {
    createSpecVersion,
    resetSpec,
    getFields,
    revertToClassicSurvey,
} from '@/api/conversation'
import {
    BranchCard,
    Card,
    CardCase,
    BlockBuilderModel,
    CardSectionIndex,
    OptionsCard,
    TYPE_CONTACT_LOOKUP,
    TYPE_REVIEW_LINK_BUTTON,
} from '@/entities/conversation'
import { blockBuilderModelToSpec } from '@/transforms/conversation'
import { ContextualData, GlobalData } from './ConversationValidator'
import { Scope } from './ConversationUtils'
import { requestFullscreen, closeFullscreen } from '@/utils/browser'
import { debounce } from '@/utils/function'
import Tabs from '@/components/Tabs.vue'
import Tab from '@/components/Tab.vue'
import BaseButton from '@/components/Rain/Buttons/BaseButton.vue'
import CardSection from './components/CardSection.vue'
import FlowReturnEditor from './components/FlowReturnEditor.vue'
import SurveyMenu from './components/SurveyMenu.vue'
import SurveyPreview from './components/SurveyPreview.vue'
import AvatarEditor from './components/AvatarEditor.vue'
import BaseFormRow from '@/components/BaseFormRow.vue'
import Modal from '@/components/Modal.vue'
import { Dict } from '@/entities'
import UpgradeModal from '@/pages/conversation/components/UpgradeModal.vue'
import { getCDNUrl } from '@/utils/cdn'
import { designSteps } from '@/pages/simple-survey/entities/design'
import { deepClone } from '@/utils/object'

const ARE_YOU_SURE =
    'Are you sure? This is a one-way transformation and can not be undone'

@Component<ConversationBlockEditorApp>({
    components: {
        BaseFormRow,
        UpgradeModal,
        Tabs,
        Tab,
        CardSection,
        FlowReturnEditor,
        SurveyMenu,
        SurveyPreview,
        BaseButton,
        Modal,
        AvatarEditor,
    },
})
export default class ConversationBlockEditorApp extends Vue {
    @Prop(String) public template!: string
    @Prop(Boolean) public hasScorecardFeature!: boolean
    @Prop(String) public defaultColor!: string
    @Prop(String) public avatarUrl?: string
    @Prop(Object) public avatarSetting!: {
        type?: string
        color?: string
        randseed?: number[]
    }

    @Getter public getSections!: BlockBuilderModel

    @Getter public getLastEdit!: number
    @Getter public getLastSave!: number
    @Getter public getContextual!: Dict<ContextualData>
    @Getter public getGlobal!: GlobalData
    @Getter public getActiveTab!: string
    @Getter public getIsStaff!: boolean
    @Getter public getIsEditSurveyTemplates!: boolean
    @Getter public getIsViewSurveyTemplates!: boolean
    @Getter public getShowUpgrade!: boolean
    @Getter public getInSubAppDesignTab?: boolean
    @Getter public getSurveyQuestions

    @Getter public getSurvey
    @Getter public getSurveyPreviews

    @Action public setLastSave!: () => void
    @Action public walkCards!: () => void
    @Action public loadSpecBlocks!: (template: string) => void
    @Action public setSections!: (sections: BlockBuilderModel) => void
    @Action public insertSection!: (data: CardSectionIndex) => void
    @Action public setFields!: (fields: Scope) => void
    @Action public setCustomFields!: (fields: Scope) => void
    @Action public activateTab!: (tabName: string) => void
    @Action public setShowUpgrade!: (boolean) => void
    @Action public updateLeadQuestionAndCsatLabels
    @Action public updateSurvey

    @Mutation public setSurveyPreviews
    @Mutation public setSurveyQuestions
    @Mutation public setUnsavedChanges

    public error = ''
    public serverErrors: string[] = []
    public initialising = false
    public submitting = false
    public resetting = false
    public fullscreen = false
    public isSidebarOpen = false
    public containerWidth = 0
    public cardSectionsWidth = 0
    public isAvatarEditorModalOpen = false
    public localSections = []
    public localSurvey = null

    // public showUpgrade = true
    public selectedAvatarUrl = '/img/avatar/noavatar.png'
    public selectedAvatarSetting: {
        type?: string
        color?: string
        randseed?: number[]
    } = {}
    public isScorecardQuestionValidationErrorMessageVisible = false

    translateX = false

    get selectedAvatarFullUrl() {
        return getCDNUrl(this.selectedAvatarUrl)
    }

    public get sectionsIndexed() {
        return this.getSections.map((section, index) => ({ section, index }))
    }

    public get masterSections() {
        return this.sectionsIndexed.filter(({ section }) => !section.flow)
    }

    public get flowSections() {
        return this.sectionsIndexed.filter(({ section }) => section.flow)
    }

    public get countTopicKeyUsed() {
        let topicCount = 0
        this.getSections.forEach((section) => {
            section.cards.forEach((card) => {
                if (card.type === 'list' && card.param === 'topic') {
                    topicCount++
                }
            })
        })
        return topicCount
    }

    public get hasScorecardQuestion(): boolean {
        let hasLinkToScorecard = false

        if (!this.getSections || this.getSections.length === 0) {
            return false
        }

        this.getSections.forEach((section) => {
            section.cards.forEach((card) => {
                if (this.hasLinkToScorecard(card)) {
                    hasLinkToScorecard = true
                }
            })
        })

        return hasLinkToScorecard
    }

    public get hasContactLookup(): boolean {
        let hasLookup = false

        if (!this.getSections || this.getSections.length === 0) {
            return false
        }

        this.getSections.forEach((section) => {
            section.cards.forEach((card) => {
                if (card.type === TYPE_CONTACT_LOOKUP) {
                    hasLookup = true
                }
            })
        })

        return hasLookup
    }

    public get hasReviewLink(): boolean {
        let hasReviewLink = false

        if (!this.getSections || this.getSections.length === 0) {
            return false
        }

        this.getSections.forEach((section) => {
            // allow adding this Question Type once.
            section.cards.forEach((card) => {
                if (card.type === TYPE_REVIEW_LINK_BUTTON) {
                    hasReviewLink = true
                }
            })
        })

        return hasReviewLink
    }

    public get saveLabel() {
        return this.submitting ? 'Saving...' : 'Save'
    }

    public get saveAndRedirectLabel() {
        return this.submitting ? 'Saving...' : 'Save and Go Back'
    }

    public get editedSinceLastSave() {
        const editedSinceLastSave = this.getLastEdit > this.getLastSave
        if (this.getInSubAppDesignTab) {
            this.setUnsavedChanges(editedSinceLastSave)
        }

        return editedSinceLastSave
    }

    public get totalErrorCount() {
        const contextual = this.getContextual
        const global = this.getGlobal

        let total = global.errors.length

        for (const cursorKey in contextual) {
            if (contextual.hasOwnProperty(cursorKey)) {
                const { errors } = contextual[cursorKey] as ContextualData
                total += errors.count
            }
        }
        return total
    }

    public get globalErrors() {
        return this.initialising ? [] : (this.getGlobal.errors as string[])
    }
    public get supportsClipboard(): boolean {
        const nav = navigator as any
        return !!(
            nav &&
            nav.clipboard &&
            nav.clipboard.readText &&
            nav.clipboard.writeText
        )
    }

    public get cardSectionsStyles() {
        if (!this.isSidebarOpen) {
            return {}
        }

        if (this.containerWidth < this.cardSectionsWidth + 436) {
            const translateX =
                (-1 * (this.containerWidth - this.cardSectionsWidth)) / 2

            return {
                transform: `translateX(${translateX}px)`,
            }
        }

        const naturalCenter = this.containerWidth / 2

        const adjustedCenter = (this.containerWidth - 436) / 2

        const translateX = -1 * (naturalCenter - adjustedCenter)

        return {
            transform: `translateX(${translateX}px)`,
        }
    }

    public get returnTo() {
        const parameters = qs.parse(window.location.search, {
            ignoreQueryPrefix: true,
        })

        return parameters.returnTo
    }

    public created() {
        this.handleResize = debounce(this.handleResize, 50, true)
    }

    public async mounted() {
        this.initialising = true

        if (this.avatarUrl) {
            this.selectedAvatarUrl = this.avatarUrl
        }
        if (this.avatarSetting) {
            this.selectedAvatarSetting = this.avatarSetting
        }

        await Promise.all([
            this.getFields(),
            this.loadSpecBlocks(this.template),
        ])
        this.localSections = deepClone(this.getSections)
        this.localSurvey = deepClone(this.getSurvey)
        this.hasContactLookup
        this.hasReviewLink

        this.handleResize()
        window.addEventListener('resize', this.handleResize)
        this.initialising = false
        this.$root.$on('on-leave-survey-confirm', async () => {
            if (this.editedSinceLastSave) {
                await this.saveSpec()
            }
        })
    }

    public beforeDestroy() {
        window.removeEventListener('resize', this.handleResize)
    }

    public async getFields() {
        const res = await getFields()
        const fieldKeys = {}
        for (const field of res.data) {
            fieldKeys[field] = true
        }
        this.setFields(fieldKeys)
        this.setCustomFields(fieldKeys)
    }

    public undoChanges() {
        this.setSections(deepClone(this.localSections))
        this.updateSurvey(deepClone(this.localSurvey))
        this.setLastSave()
    }

    public async saveSpecAndContinue() {
        let doContinue = true
        if (
            this.editedSinceLastSave ||
            (this.getInSubAppDesignTab && !this.getSurveyQuestions.completed)
        ) {
            doContinue = await this.saveSpec()
        }
        if (!doContinue) {
            return
        }
        if (this.getInSubAppDesignTab) {
            let questionCount = 0
            this.getSections.forEach((section) => {
                questionCount += section['cards'].length
            })
            this.setSurveyQuestions({
                questionCount,
                completed: true,
            })
            this.$root.$emit('reset-design-config', designSteps.surveyQuestions)
        }
    }
    public async saveSpec() {
        try {
            if (!this.error) {
                this.serverErrors = []

                if (this.getInSubAppDesignTab) {
                    if (
                        this.getSurvey.csat_neutral.length == 0 ||
                        this.getSurvey.csat_satisfied.length == 0 ||
                        this.getSurvey.csat_very_satisfied.length == 0 ||
                        this.getSurvey.csat_very_unsatisfied.length == 0 ||
                        this.getSurvey.csat_unsatisfied.length == 0
                    ) {
                        return false
                    }
                    this.submitting = true
                    await this.updateLeadQuestionAndCsatLabels()
                    // Updated local survey
                    this.updateSurvey(this.getSurvey)
                    const preview = this.getSurveyPreviews
                    preview.specChanged = this.editedSinceLastSave
                    preview.lastSave = this.getLastSave
                    preview.hasScorecardFeature = this.hasScorecardFeature
                    preview.hasScorecardQuestion = this.hasScorecardQuestion
                    this.setSurveyPreviews(preview)
                } else {
                    this.submitting = true
                }

                await createSpecVersion(
                    this.template,
                    blockBuilderModelToSpec(this.getSections)
                )
                this.setLastSave()
            }
        } catch (err: any) {
            const serverErrors =
                err.response && err.response.data && err.response.data.errors
            this.serverErrors = serverErrors
                ? Object.keys(serverErrors).map(
                      (key) => `Item at ${key}: ${serverErrors[key]}`
                  )
                : []
            this.submitting = false
            return false
        }
        this.submitting = false
        return true
    }

    public async saveSpecAndRedirect() {
        // This condition is very heavy-handed
        // It only works because currently we only ever want to return to
        // the Personal Scorecard App configuration
        if (this.returnTo && !this.hasScorecardQuestion) {
            this.isScorecardQuestionValidationErrorMessageVisible = true
            return
        }

        await this.saveSpec()

        if (!this.returnTo) {
            return
        }

        window.location.href = this.returnTo as string
    }

    public hideScorecardQuestionValidationErrorMessage() {
        this.isScorecardQuestionValidationErrorMessageVisible = false
    }

    public addSection(sectionIndex: number) {
        this.insertSection({ sectionIndex })
    }

    public async resetToDefault() {
        if (confirm(ARE_YOU_SURE)) {
            this.resetting = true
            await resetSpec(this.template, {
                default: true,
                grammar: 1,
            })
            await this.loadSpecBlocks(this.template)
            this.resetting = false
        }
    }

    public async revertToClassic() {
        if (confirm('Are you sure?')) {
            await revertToClassicSurvey(this.template)
            window.location.reload()
        }
    }

    public toggleFullscreen() {
        if (!this.fullscreen) {
            this.fullscreen = true
            requestFullscreen(this.$refs.editorApp)
        } else {
            this.fullscreen = false
            closeFullscreen()
        }
    }

    public async copyToClipboard() {
        const clipboard = (navigator as any).clipboard
        await clipboard.writeText(JSON.stringify(this.getSections))
        alert('Copied successfully')
    }

    public async pasteFromClipboard() {
        if (!navigator) {
            return
        }
        const text = await (navigator as any).clipboard.readText()
        try {
            const parsed = JSON.parse(text)
            if (parsed) {
                const errors = [] // @todo validation
                this.setSections(parsed as BlockBuilderModel)
                alert('Pasted successfully')
            }
        } catch (err) {
            alert(
                'Data pasted from clipboard is not a valid conversation specification'
            )
        }
    }

    public toggleSidebar() {
        this.translateX = true
        this.isSidebarOpen = !this.isSidebarOpen
        // Note: add the class for transform, then remove it, so that it doesn't break position:fixed for modals.
        setTimeout(() => (this.translateX = false), 1000)
    }

    public handleResize() {
        const { container, cardSectionsContainer } = this.$refs
        if (container) {
            const { width: containerWidth } = (
                this.$refs.container as HTMLElement
            ).getBoundingClientRect()
            this.containerWidth = containerWidth
        }
        if (cardSectionsContainer) {
            const { width: cardSectionsWidth } = (
                this.$refs.cardSectionsContainer as HTMLElement
            ).getBoundingClientRect()
            this.cardSectionsWidth = cardSectionsWidth
        }
    }

    public openAvatarEditorModal() {
        this.isAvatarEditorModalOpen = true
    }

    public closeAvatarEditorModal() {
        this.isAvatarEditorModalOpen = false
    }

    public closeUpgradeModal() {
        this.setShowUpgrade(false)
    }

    public handleAvatarChange({ url, setting }) {
        this.selectedAvatarUrl = url
        this.selectedAvatarSetting = setting
        this.closeAvatarEditorModal()
    }

    private hasLinkToScorecard(card: Card): boolean {
        return (
            this.isScorecardCard(card) &&
            ((card as BranchCard).cases as Array<CardCase<OptionsCard>>).filter(
                (cardCase) => {
                    const card = cardCase && cardCase.card
                    return (
                        card &&
                        card.options &&
                        card.options.length &&
                        card.options.filter((option) => option.badgeId).length >
                            0
                    )
                }
            ).length > 0
        )
    }

    private isScorecardCard(card: Card): boolean {
        return (
            (card.type === 'scorecard' || card.type === 'list') &&
            card.hasOwnProperty('match') &&
            (card as BranchCard).match === 'rating'
        )
    }
}
